import { Injectable, NgZone } from '@angular/core';
import { Observable } from 'rxjs';
import { DialogType } from '../helpers/enum/app.enums';
import { FirstUpperCase } from '../helpers/util/data-helper';
import Swal, { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';

/*  TODO: update sweetalert to latest version
    https://github.com/sweetalert2/ngx-sweetalert2#swalcomponent
    npm install sweetalert2 @sweetalert2/ngx-sweetalert2

    https://sweetalert2.github.io/#themes
    npm install @sweetalert2/theme-dark
    optional - npm install @sweetalert2/theme-borderless
    // your-app.js
    import Swal from 'sweetalert2/src/sweetalert2.js'

    // your-app.scss
    @import '~@sweetalert2/themes/dark/dark.scss';

    https://sweetalert2.github.io/recipe-gallery/
*/

@Injectable({
    providedIn: 'root',
})
export class AlertDialogService {
    constructor() {}

    confirmDialogConfig(
        title: string,
        question: string,
        dialogType: DialogType = DialogType.Question,
        subTitle = '',
        htmlMessage = '',
    ): SweetAlertOptions {
        const header = title !== undefined && title !== null ? title : '';
        const headerMessage = subTitle !== '' && subTitle !== null ? `<p class="m-b-0">${subTitle}</p>` : '';
        const message = htmlMessage !== '' && htmlMessage !== null ? `<div class="dialog-message">${htmlMessage}</div>` : '';
        return {
            title: header,
            html: `${headerMessage}${message}<p class="pt-2">${question}</p>`,
            icon: dialogType,
            showCloseButton: true,
            showCancelButton: true,
            allowOutsideClick: false,
            allowEscapeKey: false,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'btn btn-primary',
                cancelButton: 'btn btn-secondary',
            },
        };
    }

    commonDialogConfig(
        title: string,
        question: string,
        dialogType: DialogType = DialogType.Question,
        subTitle = '',
        htmlMessage = '',
    ): SweetAlertOptions {
        const header = title !== undefined && title !== null ? title : '';
        const headerMessage = subTitle !== '' && subTitle !== null ? `<p class="m-b-0">${subTitle}</p>` : '';
        const message = htmlMessage !== '' && htmlMessage !== null ? `<div class="dialog-message">${htmlMessage}</div>` : '';
        return {
            title: header,
            html: `${headerMessage}${message}<p class="pt-2">${question}</p>`,
            icon: dialogType,
            showCloseButton: true,
            allowOutsideClick: false,
            allowEscapeKey: false,
            confirmButtonText: 'Ok',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'btn btn-primary',
                cancelButton: 'btn btn-secondary',
            },
        };
    }

    resultDialog(dialogType: DialogType, message: string, title = '', htmlMessage = ''): void {
        const header = title !== '' && title !== null ? title : FirstUpperCase(dialogType);
        const headerMessage = message !== '' && message !== null ? `<p class="m-b-0">${message}</p>` : '';
        const bodyMessage = htmlMessage !== '' && htmlMessage !== null ? `<div class="dialog-message">${htmlMessage}</div>` : '';
        (async () =>
            await Swal.fire({
                title: header,
                icon: dialogType,
                html: `${headerMessage}${bodyMessage}`,
                allowEscapeKey: false,
                allowOutsideClick: false,
                buttonsStyling: false,
                customClass: {
                    confirmButton: 'btn btn-primary',
                    cancelButton: 'btn btn-secondary',
                },
            }))();
    }

    showLoading(): void {
        Swal.fire({
            title: 'Please Wait',
            allowEscapeKey: false,
            allowOutsideClick: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            },

            // timer: 3000,
            // timerProgressBar: true
        });
    }

    hideLoading(): void {
        Swal.close();
    }

    hideLoadingAsync(): Promise<void> {
        return new Promise((resolve, reject) => {
            Swal.close();
            setTimeout(() => {
                resolve();
            }, 200);
        });
    }

    errorHandler(err: any, message: string, logger: any, showmessage = true): void {
        let errorMsg = '';
        if (err.error instanceof ErrorEvent) {
            //console.log('this is client side error');
            errorMsg = `App Error`; //: ${err.error.message}`;
        } else {
            //console.log('this is server side error');
            errorMsg = `Server Error`; // Code: ${err.status},  Message: ${err.message}`;
        }
        logger.error(errorMsg);

        if (showmessage) {
            this.resultDialog(DialogType.Error, message, 'Error', errorMsg);
        }
    }

    public async displaySuccessMessage(message: string): Promise<SweetAlertResult> {
        const dialogType = DialogType.Success;
        return await Swal.fire({
            title: FirstUpperCase(dialogType),
            icon: dialogType,
            html: `<p>${message}</p>`,
            allowEscapeKey: false,
            allowOutsideClick: false,
        });
    }

    successDialogAsync = async (htmlMessage = ''): Promise<SweetAlertResult> => {
        const alert = await Swal.fire({
            titleText: 'Success',
            icon: DialogType.Success,
            html: htmlMessage,
            allowEscapeKey: false,
            allowOutsideClick: false,
            showConfirmButton: false,
            timer: 1500,
            timerProgressBar: false,
        });
        return alert;
    };

    public async displayErrorMessage(message: string): Promise<SweetAlertResult> {
        const dialogType = DialogType.Error;
        return await Swal.fire({
            title: FirstUpperCase(dialogType),
            icon: dialogType,
            html: `<p>${message}</p>`,
            allowEscapeKey: false,
            allowOutsideClick: false,
        });
    }

    errorHandlerAsync = async (err: any, message: string, logger: any, showmessage = true, htmlMessage = ''): Promise<SweetAlertResult> => {
        let errorMsg = '';
        if (err.error instanceof ErrorEvent) {
            //console.log('this is client side error');
            errorMsg =
                htmlMessage != ''
                    ? `App Error:<div>${err.error.message}</div> <div>${htmlMessage}</div>`
                    : `App Error:<div>${err.error.message}</div>`;
            logger.error('App error: ', errorMsg);
        } else {
            //console.log('this is server side error');
            errorMsg = htmlMessage != '' ? `Server error: \n<div>${htmlMessage}</div>` : '';
            logger.error('Server error: ', errorMsg);
        }
        errorMsg += `\n\n`;
        errorMsg += `<div class="faq-content p-1">Please contact Customer Care for assistance:
            <ul>
                <li>You may send a message through the portal under <a href="#/admin/contact">Contact Us</a></li>
                <li>Email us at <a [href]="onSchedulingEmailClick()">scheduling@fortecmedical.com<i class="fas fa-paper-plane text-primary f-14 pl-1"></i></a></li>
                <li>Call at <a href="tel:1-800-963-7101">1-800-963-7101<i class="fas fa-phone text-primary f-14 pl-1"></i></a> and choose <strong>option 1</strong></li>
            </ul>
        </div>`;

        if (showmessage) {
            return await this.resultDialogAsync(DialogType.Error, message, 'Error', errorMsg);
        }
        return null;
    };

    resultDialogAsync = async (dialogType: DialogType, message: string, title = '', htmlMessage = ''): Promise<SweetAlertResult> => {
        const header = title !== '' && title !== null ? title : FirstUpperCase(dialogType);
        const headerMessage = message !== '' && message !== null ? `<p class="m-b-0">${message}</p>` : '';
        const bodyMessage = htmlMessage !== '' && htmlMessage !== null ? `<div class="dialog-message">${htmlMessage}</div>` : '';
        const alert = await Swal.fire({
            titleText: header,
            icon: dialogType,
            html: `${headerMessage}${bodyMessage}`,
            allowEscapeKey: false,
            allowOutsideClick: false,
            showConfirmButton: true,
            buttonsStyling: false,
            customClass: {
                confirmButton: 'btn btn-primary',
                cancelButton: 'btn btn-secondary',
            },
        });
        return alert;
    };
}
